export type LineItemType = "generic" | "equipment" | "crew";

/**
 * Equipment rental factor scale: maps number of event days to a pricing factor.
 * Ranges are inclusive on both ends.
 */
const EQUIPMENT_FACTOR_SCALE: {
  fromDays: number;
  toDays: number;
  factor: number;
}[] = [
  { fromDays: 1, toDays: 1, factor: 1 },
  { fromDays: 2, toDays: 2, factor: 1.5 },
  { fromDays: 3, toDays: 3, factor: 1.75 },
  { fromDays: 4, toDays: 4, factor: 2 },
  { fromDays: 5, toDays: 5, factor: 2.25 },
  { fromDays: 6, toDays: 6, factor: 2.5 },
  { fromDays: 7, toDays: 7, factor: 2.75 },
  { fromDays: 8, toDays: 8, factor: 3 },
  { fromDays: 9, toDays: 9, factor: 3.25 },
  { fromDays: 10, toDays: 10, factor: 3.5 },
  { fromDays: 11, toDays: 11, factor: 3.75 },
  { fromDays: 12, toDays: 12, factor: 4 },
  { fromDays: 13, toDays: 13, factor: 4.25 },
  { fromDays: 14, toDays: 14, factor: 4.5 },
  { fromDays: 15, toDays: 21, factor: 5 },
  { fromDays: 22, toDays: 28, factor: 7 },
  { fromDays: 29, toDays: 35, factor: 8 },
  { fromDays: 36, toDays: 10000, factor: 10 },
];

export { EQUIPMENT_FACTOR_SCALE };

/** Count total calendar days between two YYYY-MM-DD date strings, inclusive. */
export function countCalendarDays(startDate: string, endDate: string): number {
  const start = new Date(startDate);
  const end = new Date(endDate);
  const diffMs = end.getTime() - start.getTime();
  return Math.floor(diffMs / 86400000) + 1;
}

/** Look up the equipment rental factor for a given number of event days. */
export function getEquipmentFactor(eventDays: number): number {
  for (const entry of EQUIPMENT_FACTOR_SCALE) {
    if (eventDays >= entry.fromDays && eventDays <= entry.toDays) {
      return entry.factor;
    }
  }
  return EQUIPMENT_FACTOR_SCALE[EQUIPMENT_FACTOR_SCALE.length - 1].factor;
}

/**
 * Compute the auto-fill factor for a given line item type and event dates.
 * Returns null if no auto-fill should happen (generic type or missing dates).
 */
export function getAutoFillFactor(
  type: LineItemType,
  eventStartDate?: string,
  eventEndDate?: string,
): number | null {
  if (type === "generic" || !eventStartDate || !eventEndDate) {
    return null;
  }
  const days = countCalendarDays(eventStartDate, eventEndDate);
  if (days < 1) return null;

  if (type === "equipment") {
    return getEquipmentFactor(days);
  }
  if (type === "crew") {
    return days;
  }
  return null;
}
